import React from "react";
import { motion } from "framer-motion";

const stories = [
  {
    tag: "The school run",
    user: "\"Backpack?\"",
    reply: "\"Under the kitchen chair.\"",
    caption:
      "Seven minutes before the bell. Shoes on. Lunchbox packed. Backpack nowhere. You call it — it calls back. You're out the door.",
  },
  {
    tag: "The late meeting",
    user: "\"Car keys?\"",
    reply: "\"Jacket pocket, by the door.\"",
    caption:
      "You're already running late and the keys have vanished. No app to open. You just ask. A warm reply tells you exactly where to look.",
  },
  {
    tag: "The lost remote",
    user: "\"Remote, where are you?\"",
    reply: "\"Between the cushions.\"",
    caption:
      "Movie night shouldn't start with a sofa excavation. Call the remote and let it answer from the folds it disappeared into.",
  },
];

const Stories = () => {
  return (
    <section
      id="stories"
      data-testid="stories-section"
      className="relative py-28 md:py-36 bg-[#FAFAFA] overflow-hidden"
    >
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] glow-blue blur-3xl opacity-30 pointer-events-none" />
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-2xl">
          <p className="text-xs tracking-[0.2em] uppercase text-blue-600 font-medium">Little Moments</p>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
            Real life, answered back.
          </h2>
          <p className="mt-5 text-zinc-600 text-lg leading-relaxed">
            Tiny everyday wins — the kind that make a morning feel calm instead of chaotic.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-3 gap-5">
          {stories.map((s, i) => (
            <motion.div
              key={s.tag}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              data-testid={`story-${i}`}
              className="group relative rounded-3xl bg-white border border-zinc-200/80 p-7 md:p-8 overflow-hidden hover:-translate-y-1 transition-transform duration-300"
            >
              <span className="text-[10px] tracking-[0.22em] uppercase text-zinc-400">{s.tag}</span>

              {/* Dialogue bubbles */}
              <div className="mt-6 space-y-3">
                <div className="flex justify-start">
                  <div className="rounded-2xl rounded-bl-sm bg-zinc-100 px-4 py-2.5 text-sm text-zinc-800 max-w-[85%]">
                    {s.user}
                  </div>
                </div>
                <div className="flex justify-end">
                  <div className="relative rounded-2xl rounded-br-sm bg-black px-4 py-2.5 text-sm text-white max-w-[85%]">
                    {s.reply}
                    {/* tiny pulsing dot */}
                    <motion.span
                      className="absolute -left-2 -top-2 w-3 h-3 rounded-full bg-blue-400"
                      animate={{ scale: [1, 1.4, 1], opacity: [0.8, 0.3, 0.8] }}
                      transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut", delay: i * 0.3 }}
                    />
                  </div>
                </div>
              </div>

              <p className="mt-6 text-sm text-zinc-600 leading-relaxed">{s.caption}</p>

              {/* Waveform footer */}
              <div className="mt-8 flex items-end gap-1 h-5">
                {Array.from({ length: 22 }).map((_, b) => (
                  <motion.span
                    key={b}
                    className="w-[3px] rounded-full bg-zinc-300 group-hover:bg-blue-400 transition-colors"
                    animate={{ height: [4, 4 + ((b * 7) % 16), 4] }}
                    transition={{ duration: 1.2 + (b % 4) * 0.12, repeat: Infinity, ease: "easeInOut", delay: b * 0.04 + i * 0.25 }}
                  />
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Stories;
